// src/layouts/landing-layout/AuthUserButton.js

import { useMsal } from "@azure/msal-react";
import React, { useState, useEffect } from "react";
import { Avatar, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Typography, CircularProgress, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Logout, Person, AccountCircle, Dashboard, Feedback, CardTravel } from "@mui/icons-material";
import { handleLogout, createUserAfterLogin } from "../../services/authService";
import { useNavigate } from "react-router-dom";
import { fetchUserRoles } from "../../services/authService";
import { FeedbackDialog } from "../../components/common/FeedbackDialog";
import SecureStorageService from "../../services/secureStorageService";

export const AuthUserButton = ({ headerTriggered = true }) => {
  const { t } = useTranslation('general');
  const [anchorEl, setAnchorEl] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const initializeUser = async () => {
      // Get the actual active account from MSAL
      const activeAccount = instance.getActiveAccount();
      
      if (activeAccount) {
        // Update local state with current account
        setCurrentUser(activeAccount);
        
        try {
          setIsLoading(true);
          
          // Use createUserAfterLogin which now properly checks for existing users first
          try {
            await createUserAfterLogin(instance, activeAccount);
          } catch (createError) {
            console.error('Error in user creation:', createError);
            // Continue even if user creation fails - they might already exist
          }
          
          // Try to get roles
          let roles = SecureStorageService.getRoles();
          
          if (!roles || !roles.length) {
            try {
              roles = await fetchUserRoles(instance, activeAccount);
              SecureStorageService.setRoles(roles);
            } catch (rolesError) {
              console.error('Error fetching roles:', rolesError);
            }
          }
          
          setUserRoles(roles || []);
          SecureStorageService.updateSessionTimestamp();
          
        } catch (error) {
          console.error('Error initializing user:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        // No active account - clear local state
        setCurrentUser(null);
        setUserRoles([]);
      }
    };

    initializeUser();
  }, [accounts, instance]);

  // Use currentUser state instead of accounts array for display
  if (!currentUser) {
    return null;
  }

  const authUser = currentUser;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDashboardClick = () => {
    handleClose();
    if (userRoles.includes('Admin')) {
      navigate('/adminDashboard');
    } else if (userRoles.includes('PartnerAdmin')) {
      navigate('/partnerDashboard');
    } else {
      navigate('/guides');
    }
  };

  let icon = <Person />;
  if (authUser?.name) {
    icon = authUser.name.split(" ")?.map(i => (i?.charAt(0) || "").toUpperCase()).join("");
  } else if (authUser?.username) {
    icon = authUser.username.split("@")[0].slice(0, 2).toUpperCase();
  }

  return (
    <Box>
      <IconButton 
        size="small" 
        onClick={handleClick}
        disabled={isLoading}
      >
        <Avatar
          size="small"
          sx={{
            bgcolor: headerTriggered ? "primary.light" : "rgba(255, 255, 255, 0.9)",
            border: 1,
            borderColor: headerTriggered ? "divider" : "rgba(0, 0, 0, 0.12)",
            color: headerTriggered ? "white" : "primary.main",
            width: 35,
            height: 35,
            fontSize: "1rem",
            position: 'relative',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: headerTriggered ? "primary.main" : "rgba(255, 255, 255, 1)",
              borderColor: headerTriggered ? "primary.main" : "rgba(0, 0, 0, 0.2)",
            }
          }}
        >
          {isLoading ? (
            <CircularProgress
              size={20}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-10px',
                marginLeft: '-10px',
                color: headerTriggered ? 'white' : 'primary.main'
              }}
            />
          ) : icon}
        </Avatar>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { minWidth: "280px", bgcolor: "#fefefe", borderRadius: 5, p: 1, boxShadow: 2 }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1, mb: 1 }}>
          <Typography noWrap fontWeight={600} sx={{ fontSize: '0.95rem' }}>
            {authUser.username || authUser.name}
          </Typography>
          {userRoles.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {userRoles.map((role) => (
                <Chip 
                  key={role}
                  label={role}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    mr: 0.5, 
                    fontSize: '0.75rem',
                    height: 20,
                    color: 'primary.main',
                    borderColor: 'primary.main'
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 1 }} />

        {/* Navigation Items */}
        <MenuItem 
          onClick={handleDashboardClick}
          disabled={isLoading}
        >
          <ListItemIcon><Dashboard /></ListItemIcon>
          {t("userMenu.dashboard")}
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            handleClose();
            navigate('/my-trips');
          }}
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            '&:hover': {
              bgcolor: 'secondary.light',
              color: 'secondary.contrastText'
            }
          }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><CardTravel /></ListItemIcon>
          My Trips
        </MenuItem>

        <MenuItem 
          onClick={() => {
            handleClose();
            navigate('/profile');
          }}
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            '&:hover': {
              bgcolor: 'secondary.light',
              color: 'secondary.contrastText'
            }
          }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><AccountCircle /></ListItemIcon>
          My Travel Profile
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {userRoles.length > 0 && (
          <>
            <MenuItem 
              onClick={() => {
                handleClose();
                setIsFeedbackOpen(true);
              }}
              disabled={isLoading}
            >
              <ListItemIcon><Feedback /></ListItemIcon>
              {t("userMenu.feedback")}
            </MenuItem>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        <MenuItem
          onClick={async () => {
            await handleLogout(instance);
            handleClose();
          }}
          disabled={isLoading}
        >
          <ListItemIcon><Logout /></ListItemIcon>
          {t("userMenu.signout")}
        </MenuItem>
      </Menu>

      <FeedbackDialog
        open={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        userEmail={authUser.username || authUser.name}
      />
    </Box>
  );
};